/**
 * Cash Flow by Week Drilldown Component
 * Phase 9.38 - Dashboard Stat Drilldowns
 *
 * Shows expected cash flow (payments due) grouped by week
 */
import { useCashFlowByWeek } from '@/hooks/useDashboardReports';
import { AlertCircle } from 'lucide-react';

interface CashFlowByWeekProps {
  weeks?: number;
}

export function CashFlowByWeek({ weeks = 8 }: CashFlowByWeekProps) {
  const { data, isLoading, error } = useCashFlowByWeek(weeks);

  if (isLoading) {
    return (
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="h-4 bg-gray-200 rounded w-1/3 mb-4 animate-pulse"></div>
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 bg-gray-100 rounded animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        <AlertCircle className="inline mr-2" size={20} />
        Failed to load cash flow data
      </div>
    );
  }

  const weeklyData = data?.data || [];
  const summary = data?.summary || {
    total_expected: 0,
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  // Calculate max amount for progress bar scaling
  const maxAmount = Math.max(...weeklyData.map((w) => w.expected_amount), 1);

  return (
    <div className="bg-white p-6 rounded-lg shadow" data-testid="cash-flow-by-week">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">
        Cash Flow by Week
      </h2>

      {/* Summary Card */}
      <div className="bg-green-50 p-4 rounded-lg mb-6">
        <div className="text-sm text-gray-600">Total Expected ({weeks} weeks)</div>
        <div className="text-2xl font-bold text-green-600" data-testid="summary-total">
          {formatCurrency(summary.total_expected)}
        </div>
      </div>

      {/* Weekly Breakdown */}
      {weeklyData.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Week</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Expected</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Invoices</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-1/3">Distribution</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {weeklyData.map((week, index) => (
                <tr key={week.week_start} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">
                    {week.week_label}
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-right text-green-600">
                    {formatCurrency(week.expected_amount)}
                  </td>
                  <td className="px-4 py-3 text-sm text-center text-gray-600">
                    {week.invoice_count} {week.invoice_count === 1 ? 'invoice' : 'invoices'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center">
                      <div className="flex-1 bg-gray-200 rounded-full h-4">
                        <div
                          className="bg-green-500 h-4 rounded-full transition-all duration-300"
                          style={{ width: `${(week.expected_amount / maxAmount) * 100}%` }}
                          data-testid={`progress-bar-${index}`}
                        />
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500">
          No expected payments in the next {weeks} weeks
        </div>
      )}
    </div>
  );
}
